import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { CheckCircle2, Crown, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { openRazorpay } from "@/lib/razorpay";

export default function SubscribePage() {
  const [plans, setPlans] = useState({});
  const [chosen, setChosen] = useState("monthly");
  const [form, setForm] = useState({ name: "", email: "" });
  const [processing, setProcessing] = useState(false);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    api.get("/subscription/plans").then((r) => setPlans(r.data));
    const t = localStorage.getItem("sub_token");
    if (t) api.get("/subscription/status", { params: { token: t } }).then((r) => setCurrent(r.data));
  }, []);

  const buy = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("नाम और ईमेल दें"); return; }
    setProcessing(true);
    try {
      const { data } = await api.post("/subscription/create", { ...form, plan: chosen });
      const res = await openRazorpay({
        order_id: data.order_id, amount: data.amount, currency: data.currency, key_id: data.key_id,
        description: `INDIAN-NEWS-21 ${chosen === "monthly" ? "Monthly" : "Yearly"}`,
        prefill: { name: form.name, email: form.email },
      });
      if (res.ok) {
        // Verify separately for subscription
        const { data: verify } = await api.post("/subscription/verify", {
          razorpay_order_id: data.order_id,
          razorpay_payment_id: res.data?.razorpay_payment_id || "",
          razorpay_signature: res.data?.razorpay_signature || "",
        });
        // Actually res.data (from generic verify) already handled generic — but subscription needs its own verify.
        // The openRazorpay handler already called /api/payments/verify. Fallback: also call sub/verify.
        if (verify?.token) localStorage.setItem("sub_token", verify.token);
        toast.success("धन्यवाद! अब आप ad-free पढ़ सकते हैं");
        setCurrent({ active: true, plan: chosen, expires_at: verify.expires_at });
      }
    } catch (e) { toast.error(formatError(e)); }
    finally { setProcessing(false); }
  };

  return (
    <>
      <PublicHeader />
      <section className="bg-gradient-to-br from-[#0F172A] via-[#111827] to-[#1E3A8A] text-white">
        <div className="max-w-4xl mx-auto px-4 py-14 text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Crown className="w-6 h-6 text-amber-400"/>
            <span className="uppercase tracking-[0.28em] text-[11px] text-amber-300 font-black">Premium · Ad-Free Reading</span>
          </div>
          <h1 className="font-devnagri-heading text-4xl md:text-5xl font-black">बिना विज्ञापन <span className="text-amber-300">पढ़ें</span></h1>
          <p className="text-slate-300 mt-3 max-w-xl mx-auto">महीने में सिर्फ़ ₹99 में विज्ञापन-मुक्त, विशेष संपादकीय एवं प्राथमिकता समर्थन।</p>
        </div>
      </section>
      <main className="max-w-4xl mx-auto px-4 py-10" data-testid="subscribe-page">
        {current?.active ? (
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-6 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3"/>
            <div className="font-bold text-emerald-800">आप {current.plan === "monthly" ? "मासिक" : "वार्षिक"} subscriber हैं</div>
            <div className="text-sm text-emerald-700 mt-1">वैध: {new Date(current.expires_at).toLocaleDateString("hi-IN")}</div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {Object.entries(plans).map(([id, p]) => (
                <button key={id} onClick={() => setChosen(id)}
                  className={`text-left p-6 rounded-lg border-2 transition ${chosen === id ? "border-amber-500 bg-amber-50" : "border-slate-200 bg-white hover:border-slate-400"}`}
                  data-testid={`plan-${id}`}>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{p.label}</div>
                  <div className="flex items-baseline gap-1 mt-2">
                    <span className="text-4xl font-black text-[#0F172A]">₹{p.price}</span>
                    <span className="text-sm text-slate-500">/ {p.days} दिन</span>
                  </div>
                  {id === "yearly" && <div className="mt-2 inline-block text-[10px] uppercase tracking-widest bg-amber-500 text-white font-bold px-2 py-0.5 rounded">17% छूट</div>}
                  <ul className="mt-4 space-y-1 text-xs text-slate-600">
                    <li className="flex items-center gap-1.5"><Shield className="w-3 h-3 text-emerald-500"/>विज्ञापन-मुक्त</li>
                    <li className="flex items-center gap-1.5"><Shield className="w-3 h-3 text-emerald-500"/>विशेष संपादकीय पहले</li>
                    <li className="flex items-center gap-1.5"><Shield className="w-3 h-3 text-emerald-500"/>प्राथमिकता समर्थन</li>
                  </ul>
                </button>
              ))}
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-3">
              <Input placeholder="नाम *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sub-name"/>
              <Input type="email" placeholder="ईमेल *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sub-email"/>
              <Button onClick={buy} disabled={processing} className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-[#0F172A] font-bold py-6 text-lg" data-testid="sub-buy">
                <Crown className="w-5 h-5 mr-2"/>{processing ? "प्रक्रिया…" : `₹${plans[chosen]?.price || 0} भुगतान करें`}
              </Button>
            </div>
          </>
        )}
      </main>
      <PublicFooter />
    </>
  );
}
